import type { JSX } from 'react';
import { Alert, Badge, Button, Group, Paper, SimpleGrid, Stack, Switch, Text } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { PortalRoundView } from '@pst/shared';
import { ApiError, portalJobsApi } from '../api';
import { FETCH_PORTALS, NIGHT_BLOCKERS, PORTAL_JOB_STATES, PORTAL_NIGHT, formatDateTime, formatDayOf, nightsLeft } from '../format';

const message = (e: unknown): string => (e instanceof ApiError || e instanceof Error ? e.message : 'Fehlgeschlagen.');
const clock = (iso: string): string => new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

/**
 * Where the night round of one portal stands (PLAN-PORTALE.md 3.4b): the
 * switch, the next run or the one reason why not, the ramp week, the last
 * chunks and the progress through the list. Under Verbindungen and Abzüge.
 */
export function PortalRound({ round, showTitle = false }: { round: PortalRoundView; showTitle?: boolean }): JSX.Element {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['portal-jobs'] });
  const night = useMutation({ mutationFn: (enabled: boolean) => portalJobsApi.setNight(round.portal, enabled), onSettled: refresh });
  const confirm = useMutation({ mutationFn: () => portalJobsApi.confirmRamp(round.portal), onSettled: refresh });
  const { ramp, progress } = round;
  const waiting = round.blocker === 'dienst';
  return (
    <Paper withBorder p="sm" radius="md">
      <Stack gap="xs">
        {showTitle && <Text size="sm" fw={600}>{FETCH_PORTALS[round.portal].label}</Text>}
        <Switch size="sm" label={PORTAL_NIGHT.label} description={PORTAL_NIGHT.hint} checked={round.nightEnabled} disabled={night.isPending}
          onChange={(e) => night.mutate(e.currentTarget.checked)} />
        {night.isError && <Alert color="red" py={6}>{message(night.error)}</Alert>}
        {round.nightEnabled && (
          <>
            <Text size="sm">
              {round.nextRun ? <>Nächster Nachtlauf: <b>{formatDateTime(round.nextRun.start)}–{clock(round.nextRun.end)} Uhr</b></> : 'Kein Nachtlauf'}
              {round.blocker && <Text span size="sm" c={waiting ? 'orange' : 'dimmed'}> – {NIGHT_BLOCKERS[round.blocker]}</Text>}
            </Text>
            {ramp.confirmedAt ? (
              <Text size="xs" c="dimmed">Anlaufwoche beendet am {formatDayOf(ramp.confirmedAt)}: volles Tageslimit ({ramp.effectiveLimit} Seitenaufrufe).</Text>
            ) : (
              <Group gap="xs" wrap="wrap">
                <Badge variant="light" color="yellow">halbes Tageslimit: {ramp.effectiveLimit}</Badge>
                <Text size="xs" c="dimmed">
                  {ramp.until ? `Anlaufwoche bis ${formatDayOf(ramp.until)}` : 'Die Anlaufwoche beginnt mit dem ersten Häppchen'}
                  {ramp.reportSentAt ? `; Bericht verschickt am ${formatDayOf(ramp.reportSentAt)}.` : '; danach Bericht per E-Mail und ntfy.'}
                </Text>
                {ramp.reportSentAt && (
                  <Button size="xs" variant="light" loading={confirm.isPending} onClick={() => { if (window.confirm('Den Bericht gelesen? Ab dem nächsten Häppchen gilt das volle Tageslimit.')) confirm.mutate(); }}>
                    Anlauf beenden, volles Tageslimit
                  </Button>
                )}
              </Group>
            )}
            {confirm.isError && <Alert color="red" py={6}>{message(confirm.error)}</Alert>}
          </>
        )}
        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="xs">
          <Fact label="bekannt" value={progress.known} />
          <Fact label="geholt" value={progress.fetched} />
          <Fact label="offen" value={progress.open} />
          <Fact label="ältestes Abrufdatum" value={progress.oldest ? formatDayOf(progress.oldest) : '–'} />
        </SimpleGrid>
        <Text size="xs" c="dimmed">
          {nightsLeft(progress.nightsLeft)}{progress.missing ? ` · ${progress.missing} nicht mehr gefunden` : ''}
          {progress.listedAt ? ` · Liste gesammelt am ${formatDayOf(progress.listedAt)}` : ' · Liste noch nicht gesammelt'}
        </Text>
        {round.recent.length > 0 && (
          <Stack gap={2}>
            <Text size="xs" fw={600}>Letzte Häppchen</Text>
            {round.recent.map((c) => (
              <Group key={c.id} gap="xs" wrap="nowrap" align="flex-start">
                <Badge size="xs" variant="light" color={PORTAL_JOB_STATES[c.state].color} style={{ flexShrink: 0 }}>{PORTAL_JOB_STATES[c.state].label}</Badge>
                <Text size="xs" style={{ overflowWrap: 'anywhere' }}>{formatDateTime(c.createdAt)} · {c.pages} Seiten{c.message ? ` · ${c.message}` : ''}</Text>
              </Group>
            ))}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}

function Fact({ label, value }: { label: string; value: number | string }): JSX.Element {
  return (
    <Stack gap={0}>
      <Text size="sm" fw={600}>{typeof value === 'number' ? value.toLocaleString('de-DE') : value}</Text>
      <Text size="xs" c="dimmed">{label}</Text>
    </Stack>
  );
}
